import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { LazyLoadEvent, ScrollerOptions, SelectItem } from "primeng/api";
import { ReportingPeriods } from "../reporting-periods";
import { EntityTypes } from "../entity-types";
import { ViewRollupArtifactResponse } from "../rollup-artifact.model";
import { StatusTypes } from "../status-types";
import { AppContext } from "../../app.context";
import { MessageCodes } from "../message-codes";
import { NgMessage } from "../ng-message";
import { AppMessageService } from "../../services/app-message.service";
import { GridProcessor } from "../grid.processor";
import { GridColumn, GridFilter } from "../grid.model";
import { fieldof, formatCDSCode } from "../string-operations";
import { EntitiesByTypeRequest, EntityResponse } from "../entity.model";
import { Dropdown } from "primeng/dropdown";
import { FiscalYearFormat, FiscalYearReportingPeriodsResponse } from "../fiscal-year.model";
import { FilterArtifacts } from "../base-artifact.model";
import { EntitiesService } from '../../services/entities.service';

@Component({
  selector: 'app-data-explorer-filter',
  templateUrl: './data-explorer-filter.component.html',
  styleUrls: ['./data-explorer-filter.component.scss']
})

export class DataExplorerFilterComponent implements OnInit {
  @ViewChild('fiscalYearDropdown', { static: true }) fiscalYearDropdown: Dropdown;
  @ViewChild('reportingPeriodDropdown', { static: true }) reportingPeriodDropdown: Dropdown;
  @ViewChild('entityTypeDropdown', { static: true }) entityTypeDropdown: Dropdown;
  @ViewChild('entitiesDropdown', { static: true }) entitiesDropdown: Dropdown;

  readonly unknownErrorMessage: NgMessage;

  @Input() loadingFiscalYears : boolean = true;
  @Input() fiscalYearOptions: Array<FiscalYearReportingPeriodsResponse>;

  @Input() loadingReportingPeriods: boolean = false;
  @Input() reportingPeriodOptions: Array<SelectItem>;

  @Input() loadingEntityTypes: boolean = false;
  @Input() entityTypeOptions: Array<SelectItem<EntityTypes>>;

  @Input() loadingEntities: boolean = false;

  @Input() rollupDataExtract: ViewRollupArtifactResponse;
  @Input() rollupDatImport: ViewRollupArtifactResponse;

  selectedFiscalYear: FiscalYearFormat;
  selectedReportingPeriod: ReportingPeriods;
  selectedEntityType: EntityTypes;
  selectedCDSCode: string;

  @Output() fiscalYearChangeEvent = new EventEmitter<FiscalYearFormat>();
  @Output() reportingPeriodChangeEvent = new EventEmitter();
  @Output() applyFiltersEvent = new EventEmitter<FilterArtifacts>();
  @Output() downloadRollupBlobEvent = new EventEmitter<ViewRollupArtifactResponse>();

  data: Array<SelectItem>;
  cols: Array<GridColumn>;
  globalFilterFields: Array<string>;
  globalFilterMatchMode: string;

  options: ScrollerOptions = {
    lazy: true,
    onLazyLoad: this.onLazyLoad.bind(this),
  };

  selectedEntityTypeCDE: boolean = false;
  selectedEntityTypeSBE: boolean = false;
  rollupAvailableTextDesc: string;

  constructor(
    readonly appContext: AppContext,
    readonly appMessageService: AppMessageService,
    readonly entitiesService: EntitiesService) {
    this.unknownErrorMessage = this.appContext.messages[MessageCodes.UnknownError];
    this.cols = this.createCols();
  }

  ngOnInit() {
    this.fiscalYearDropdown.hide();
    this.reportingPeriodDropdown.hide();
    this.entityTypeDropdown.hide();
    this.entitiesDropdown.hide();
  }

  fiscalYearChange(): void {
    this.selectedReportingPeriod = undefined;
    this.selectedEntityType = undefined;
    this.selectedCDSCode = undefined;
    this.selectedEntityTypeCDE = false;
    this.selectedEntityTypeSBE = false;
    this.fiscalYearChangeEvent.next(this.selectedFiscalYear)
  }

  reportingPeriodChange(): void {
    this.selectedEntityType = undefined;
    this.selectedCDSCode = undefined;
    this.selectedEntityTypeCDE = false;
    this.selectedEntityTypeSBE = false;
    this.reportingPeriodChangeEvent.next(null);
  }

  entityTypeChange(): void {
    this.selectedCDSCode = undefined;
    this.selectedEntityTypeCDE = this.selectedEntityType == EntityTypes.CaliforniaDepartmentOfEducation;
    this.selectedEntityTypeSBE = this.selectedEntityType == EntityTypes.StateBoardOfEducation;
  }

  applyFilters(): void {
    if (this.selectedEntityTypeCDE) {
      this.rollupAvailableTextDesc = "All available records for all entities in California in the "
      this.selectedCDSCode = "99000000000000";
    } else if(this.selectedEntityTypeSBE) {
      this.rollupAvailableTextDesc = "All available records for all charter schools under the oversight of the State Board of Education in the "
      this.selectedCDSCode = "00000000000000";
    } else
      this.rollupAvailableTextDesc = "All available records for all entities in the county in the "

    const filterArtifacts = new FilterArtifacts({
      fullFiscalYear: this.selectedFiscalYear.fullFiscalYear,
      reportingPeriod: this.selectedReportingPeriod,
      cdsCode: this.selectedCDSCode,
      entityType: this.selectedEntityType
    });

    this.applyFiltersEvent.next(filterArtifacts);
  }

  downloadRollupBlob(artifact: ViewRollupArtifactResponse): void {
    this.downloadRollupBlobEvent.next(artifact);
  }

  onLazyLoad(event: LazyLoadEvent): void {
    if (this.selectedEntityTypeCDE)
      return;

    this.loadingEntities = true;

    const request = this.createEntitiesRequest();
    const itemsRequest = GridProcessor.createFilterSortPageRequest(
      request,
      event,
      this.cols,
      this.globalFilterFields,
      this.globalFilterMatchMode);

    this.entitiesService.fetchEntitiesByType(this.appContext.createRequest(itemsRequest))
      .subscribe(ngResponse => {
        if (!ngResponse)
          this.appMessageService.add({ key:'responseToast', severity: this.unknownErrorMessage.severityLevel, summary: this.unknownErrorMessage.text });
        else if (ngResponse.status === StatusTypes.Success) {
          this.data = ngResponse.response.results.map(entity => {
              return { label: `${formatCDSCode(entity.cdsCode)} ${entity.name}`, value: entity.cdsCode } as SelectItem<string>;
            });
        }
        else
          this.appMessageService.add({ key:'responseToast', severity: ngResponse.error.severityLevel, summary: ngResponse.error.failureReason });
        this.loadingEntities = false;
      });
  }

  private createEntitiesRequest(): EntitiesByTypeRequest {
    return new EntitiesByTypeRequest({
      caFiscalYear: this.selectedFiscalYear.caFiscalYear,
      entityType: this.selectedEntityType
    });
  }

  private createCols(): Array<GridColumn> {
    return [
      new GridColumn( {
        header: 'CDSCode',
        field: fieldof<EntityResponse>('cdsCode'),
        filter: new GridFilter()
      }),
      new GridColumn( {
        header: 'Name',
        field: fieldof<EntityResponse>('name'),
        filter: new GridFilter()
      })
    ];
  }
}
